import React, {memo} from "react"
import {useTranslation} from "react-i18next";

import FormSection from "../../../components/FormSection";
import {formikPropertyChanged, generateMeasurementKey, groupFractureSubType} from "./index";
import CollectionsTabList from "./CollectionsTabList";
import Model23MeasureField from "./Model23MeasureField";
import MeasurementsList, {renderHeader} from "./MeasurementsList";
import Model23Select from "./Model23Select";
import Stack from "@mui/material/Stack";


const ExtendedColumnTests = memo(({formik, emptyTest, emptyMeasure, model23Registry, inputDisabled}) => {
    const {t} = useTranslation(['models23', 'enums']);

    const onTestsUpdate = (tests) => {
        formik.setFieldValue('extendedColumnTests', tests);
    }

    const onMeasurementsUpdate = (profileIndex, measurements) => {
        formik.setFieldValue(`extendedColumnTests[${profileIndex}].measurements`, measurements);
    }

    const createEmptyMeasure = () => {
        return {...emptyMeasure, _key: generateMeasurementKey(),}
    }

    const renderTest = (testIndex) => {
        return <ColumnTest formik={formik}
                           testIndex={testIndex}
                           model23Registry={model23Registry}
                           createEmptyMeasure={createEmptyMeasure}
                           onMeasurementsUpdate={(measurements) => onMeasurementsUpdate(testIndex, measurements)}
                           inputDisabled={inputDisabled}/>
    }

    return <FormSection title={t('model23Form.extendedColumnTests')}
                        openDefault={false}
                        error={Boolean(formik.errors.extendedColumnTests) && formik.touched.extendedColumnTests}>
        <CollectionsTabList collections={formik.values.extendedColumnTests}
                            renderCollection={renderTest}
                            emptyCollection={emptyTest}
                            onCollectionsUpdate={onTestsUpdate}
                            collectionType="test"
                            inputDisabled={inputDisabled}/>
    </FormSection>
}, (prevProps, nextProps) => formikPropertyChanged(prevProps, nextProps, 'extendedColumnTests') && prevProps.model23Registry === nextProps.model23Registry);


const ColumnTest = ({formik, testIndex, createEmptyMeasure, onMeasurementsUpdate, model23Registry, inputDisabled}) => {
    const ectAttributesRegistry = model23Registry.data?.extendedColumnTestAttributes;
    const extendedColumTestRegistry = model23Registry.data?.extendedColumnTestRegistry;
    const ECTCodeRegistry = extendedColumTestRegistry?.ectCode;
    const fractureTypeRegistry = extendedColumTestRegistry?.fractureType;
    const fractureSubTypeRegistry = extendedColumTestRegistry?.fractureSubType;
    const fractureSubTypeByType = groupFractureSubType(fractureSubTypeRegistry);

    const test = formik.values.extendedColumnTests[testIndex];
    const testPrefix = `extendedColumnTests[${testIndex}]`

    const headers = ['ectCode', 'fractureType', 'qualityShear', 'depth', 'score', 'tdh'].map(header => {
        const name = ectAttributesRegistry?.[header]?.name ?? header;
        const info = ectAttributesRegistry?.[header]?.description ?? '';
        return renderHeader(name, info);
    })

    const renderMeasure = (row, index) => {

        const handleFractureSelect = (e) => {
            const fractureType = e.target.value;
            let qualityShear = null;

            const filteredFractureSubTypes = fractureSubTypeByType[fractureType] ?? [];

            if(filteredFractureSubTypes.length === 1){
                qualityShear = filteredFractureSubTypes[0].code;
            }

            const newMeasurement = {
                ...row,
                fractureType: fractureType,
                qualityShear: qualityShear,
            }

            // I have to update the entire measurement to prevent rendering issues with Formik and updates of dependent
            // fields.
            formik.setFieldValue(`${testPrefix}.measurements[${index}]`, newMeasurement);
        }

        return <>
            <Model23Select name={`${testPrefix}.measurements[${index}].ectCode`}
                           label=""
                           labelFn={r => `${r.code} - ${r.description}`}
                           formik={formik}
                           registry={ECTCodeRegistry}
                           disabled={inputDisabled}/>
            <Model23Select name={`${testPrefix}.measurements[${index}].fractureType`}
                           label=""
                           labelFn={r => `${r.code} - ${r.description}`}
                           formik={formik}
                           onChange={handleFractureSelect}
                           registry={fractureTypeRegistry}
                           disabled={inputDisabled}/>
            <Model23Select name={`${testPrefix}.measurements[${index}].qualityShear`}
                           label=""
                           labelFn={r => `${r.code} - ${r.description}`}
                           formik={formik}
                           disabled={row.fractureType === null || row.fractureType === undefined || inputDisabled}
                           registry={fractureSubTypeByType[row.fractureType] ?? []}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`${testPrefix}.measurements[${index}].depth`}
                                 unitOfMeasure="cm"
                                 type="number"
                                 disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`${testPrefix}.measurements[${index}].score`}
                                 unitOfMeasure=""
                                 type="number"
                                 disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`${testPrefix}.measurements[${index}].tdh`}
                                 unitOfMeasure="cm"
                                 type="number"
                                 disabled={inputDisabled}/>
        </>
    }

    return <Stack spacing={2}>
        <MeasurementsList headers={headers}
                             measurements={test.measurements}
                             createEmptyMeasure={createEmptyMeasure}
                             onMeasurementsUpdate={onMeasurementsUpdate}
                             renderMeasure={renderMeasure}
                             inputDisabled={inputDisabled}/>
        </Stack>
}

export default ExtendedColumnTests;